// src/components/Layers.js
import React, { useSate } from "react";
import {
  bringLayerForward,
  sendLayerBackward,
  sendLayerToTop,
  sendLayerToBottom,
} from "../helpers/moveLayers";

function Layers({ layersRef, drawCanvas, forceUpdateLayers, layersVersion }) {
  const handleDeleteLayer = (realIndex) => {
    layersRef.current.splice(realIndex, 1);
    drawCanvas();
    forceUpdateLayers();
  };

  const handleBringLayerForward = (realIndex) => {
    layersRef.current.forEach((l) => (l.isSelected = false));
    layersRef.current[realIndex].isSelected = true;
    bringLayerForward(layersRef, drawCanvas, forceUpdateLayers);
  };

  const handleSendLayerBackward = (realIndex) => {
    layersRef.current.forEach((l) => (l.isSelected = false));
    layersRef.current[realIndex].isSelected = true;
    sendLayerBackward(layersRef, drawCanvas, forceUpdateLayers);
  };

  const handleSendLayerToTop = (realIndex) => {
    layersRef.current.forEach((l) => (l.isSelected = false));
    layersRef.current[realIndex].isSelected = true;
    sendLayerToTop(layersRef, drawCanvas, forceUpdateLayers);
  };

  const handleSendLayerToBottom = (realIndex) => {
    layersRef.current.forEach((l) => (l.isSelected = false));
    layersRef.current[realIndex].isSelected = true;
    sendLayerToBottom(layersRef, drawCanvas, forceUpdateLayers);
  };

  // Reverse array so "top" layer is shown first
  const reversedLayers = [...layersRef.current].reverse();
  const hasLayers = reversedLayers.length > 0;

  return (
    <div className="text-left">
      {hasLayers && (
        <>
      <h2 className="whitespace-nowrap text-sm md:text-lg text-monadWhite ml-2">Top</h2>
      <div className="flex flex-col gap-3">
        {reversedLayers.map((layer, revIndex) => {
          const realIndex = layersRef.current.length - 1 - revIndex;
          const labelIndex = revIndex + 1; // For display

          // Build preview...
          if (layer.type === "asset") {
            return (
              <LayerRow
                key={realIndex}
                realIndex={realIndex}
                labelIndex={labelIndex}
                layerType="asset"
                preview={
                  <img
                    src={layer.image?.src}
                    alt="layer thumbnail"
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "cover",
                      border: "1px solid #999",
                    }}
                  />
                }
                handleDeleteLayer={handleDeleteLayer}
                handleSendLayerToTop={handleSendLayerToTop}
                handleBringLayerForward={handleBringLayerForward}
                handleSendLayerBackward={handleSendLayerBackward}
                handleSendLayerToBottom={handleSendLayerToBottom}
              />
            );
          } else if (layer.type === "text") {
            // Limit to 5 chars
            const rawText = layer.text || "";
            const truncatedText =
              rawText.length > 5 ? rawText.slice(0, 5) + "…" : rawText || "(Empty Text)";

            // Contrasting background
            const textColor = layer.color ?? "#000";
            const isWhiteText = textColor.toLowerCase().includes("fff");
            const bgColor = isWhiteText ? "#333" : "#fff";

            const styleForPreview = {
              display: "inline-block",
              padding: "2px 4px",
              border: "1px solid #999",
              backgroundColor: bgColor,
              color: textColor,
              fontFamily: layer.fontFamily || "Impact",
              fontWeight: layer.isBold ? "bold" : "normal",
              fontStyle: layer.isItalic ? "italic" : "normal",
              textTransform: layer.isAllCaps ? "uppercase" : "none",
              textDecoration: [
                layer.isUnderlined ? "underline" : "",
                layer.isStrikethrough ? "line-through" : "",
              ]
                .filter(Boolean)
                .join(" "),
              fontSize: Math.max(10, layer.fontSize * 0.5),
              lineHeight: "1.2",
              maxWidth: "150px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              cursor: "default",
            };

            return (
              <LayerRow
                key={realIndex}
                realIndex={realIndex}
                labelIndex={labelIndex}
                layerType="text"
                preview={<span style={styleForPreview}>{truncatedText}</span>}
                handleDeleteLayer={handleDeleteLayer}
                handleSendLayerToTop={handleSendLayerToTop}
                handleBringLayerForward={handleBringLayerForward}
                handleSendLayerBackward={handleSendLayerBackward}
                handleSendLayerToBottom={handleSendLayerToBottom}
              />
            );
          }

          return null;
        })}

        {/* Text "Bottom" below all the listed layers */}
        <h2 className="whitespace-nowrap text-sm md:text-lg text-monadWhite ml-2">Bottom</h2>
      </div>
      </>
      )}
    </div>
  );
}

function LayerRow({
  realIndex,
  labelIndex,
  layerType,
  preview,
  handleDeleteLayer,
  handleSendLayerToTop,
  handleBringLayerForward,
  handleSendLayerBackward,
  handleSendLayerToBottom,
}) {
  return (
    <div className="
         flex items-center gap-1 
         bg-monadDark text-white 
         p-2 rounded w-full
         min-w-0
         md:min-w-[500px]
       ">
      {preview}
      <span className="text-sm">
        Layer {labelIndex} ({layerType})
      </span>

      {/* Delete Button */}
      <button
        onClick={() => handleDeleteLayer(realIndex)}
        className="    ml-auto 
    bg-red-500 text-white 
    hover:bg-red-600 transition-colors
    rounded
    
    /* Base: smaller for desktops/tablets */
    px-3 py-1 text-sm leading-none h-10

    /* max-md: bigger on screens below md */
    max-md:px-2 max-md:py-0 max-md:text-2xl max-md:h-8"
        title="Delete this layer"
      >
        ×
      </button>

      {/* Send to Top */}
      <button
        onClick={() => handleSendLayerToTop(realIndex)}
        className="px-3 py-1 rounded
                   bg-monadDark text-white
                   hover:bg-monadAccent transition-colors
                   max-md:px-1 max-md:py-0 max-md:text-sm max-md:leading-none max-md:h-11"
        title="Send to Top"
      >
        Top
      </button>

      {/* Bring Layer Forward */}
      <button
        onClick={() => handleBringLayerForward(realIndex)}
        className="px-3 py-1 rounded
                   bg-monadDark text-white
                   hover:bg-monadAccent transition-colors
                   max-md:px-1 max-md:py-0 max-md:text-sm max-md:leading-none max-md:h-11"
        title="Move Up One"
      >
        ↑
      </button>

      {/* Send Layer Backward */}
      <button
        onClick={() => handleSendLayerBackward(realIndex)}
        className="px-3 py-1 rounded
                   bg-monadDark text-white
                   hover:bg-monadAccent transition-colors
                   max-md:px-1 max-md:py-0 max-md:text-sm max-md:leading-none max-md:h-11"
        title="Move Down One"
      >
        ↓
      </button>

      {/* Send to Bottom */}
      <button
        onClick={() => handleSendLayerToBottom(realIndex)}
        className="px-3 py-1 rounded
                   bg-monadDark text-white
                   hover:bg-monadAccent transition-colors
                   max-md:px-1 max-md:py-0 max-md:text-sm max-md:leading-none max-md:h-11"
        title="Send to Bottom"
      >
        Bottom
      </button>
    </div>
  );
}


export default Layers;
